import {
  CartesianGrid,
  ComposedChart,
  Label,
  LabelList,
  Legend,
  LegendProps,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts'
import { useTooltipContext } from '../BaseTooltip/CustomTooltip'
import { renderLineLegend } from './components/LineChartLegend'
import { CustomLabel } from './components/CustomLable'

export interface RechartsProps {
  key: string
  yAxisId?: string
  color: string
  display: boolean
  isHover: boolean
}

interface BaseLineChartProps<T> {
  data: T[]
  colors: any
  hideValue: boolean
  chartProps: Array<RechartsProps>
  yAxisProps?: Array<{
    yAxisId: string
    orientation: 'left' | 'right'
    unit: string
  }>
  width?: string | number
  height?: string | number
  margin?: {
    top?: number
    bottom?: number
    right?: number
    left?: number
  }
  legendProps?: Pick<
    LegendProps,
    'height' | 'width' | 'layout' | 'align' | 'margin' | 'formatter' | 'iconType' | 'iconSize' | 'onClick'
  >
}

function BaseLineChart<T>({
  data,
  hideValue,
  colors,
  width = '100%',
  height = '100%',
  chartProps,
  yAxisProps,
  ...props
}: BaseLineChartProps<T>) {
  const { openTooltip, closeTooltip } = useTooltipContext()

  const tooltipContent = (e: any) => {
    return <span>{e && e.toFixed(2)}</span>
  }

  return (
    <ResponsiveContainer width={width} height={height}>
      <ComposedChart
        data={data}
        {...props}
        margin={{
          left: 10,
          top: 30
        }}
      >
        <CartesianGrid strokeDasharray='3 3' />
        <XAxis dataKey='name' padding={{ left: 100, right: 100 }} allowDecimals={false} />
        {yAxisProps ? (
          yAxisProps.map((cp) => {
            return (
              <YAxis
                allowDecimals={false}
                key={cp.yAxisId}
                yAxisId={cp.yAxisId}
                orientation={cp.orientation}
                unit={cp.unit}
                allowDataOverflow
              />
            )
          })
        ) : (
          <YAxis allowDecimals={false} />
        )}
        <Legend content={renderLineLegend} />
        {chartProps.map((cp) => {
          return (
            <Line
              key={cp.key}
              yAxisId={cp.yAxisId}
              dataKey={cp.key}
              hide={!cp.display}
              strokeWidth={2}
              fill={colors?.[cp.key]}
              stroke={colors?.[cp.key]}
              dot={{
                onMouseMove: (e: any) =>
                  hideValue
                    ? openTooltip({
                        content: tooltipContent(e.value)
                      })
                    : null,
                onMouseLeave: () => closeTooltip()
              }}
            >
              {!hideValue && <LabelList dataKey={cp.key} position='top' />}
            </Line>
          )
        })}
        <ReferenceLine
          x={(data as any)?.[0]?.name}
          label={(e) => CustomLabel({ ...e, title: 'No.', dy: 20, dx: 40 })}
        />
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export default BaseLineChart
